'use client'

import { memo, useMemo } from 'react'
import {
  DELIVERY_STOPS,
  NORTH_POLE_LAT,
  NORTH_POLE_LON,
  ROUTE,
  stopStatusAt,
  type SleighPosition,
  type TrackerCopy,
  type TrackerState,
} from '@/lib/santa-tracker'
import {
  GRID_MERIDIANS,
  GRID_PARALLELS,
  LANDMASS_PATHS,
  MAP_VIEWBOX,
  MAP_VIEWBOX_ATTR,
  MAP_WIDTH,
  buildRoutePath,
  buildStars,
  project,
  projectX,
  projectY,
} from '@/lib/santa-tracker/world-map'

/**
 * Mapa mundial del tracker.
 *
 * **Accesibilidad.** El `<svg>` es `role="img"` con una descripción textual del
 * estado, y sus marcadores NO son focalizables. No es un descuido: 58 paradas
 * serían 58 paradas de tabulación antes de llegar al siguiente enlace. El
 * camino accesible equivalente es la lista de la ruta
 * (`tracker-route-list.tsx`), donde cada parada es un `<button>` de verdad y
 * abre exactamente el mismo panel. Quien navega con ratón o pantalla táctil usa
 * el mapa; quien navega con teclado o lector de pantalla, la lista.
 */

const STARS = buildStars(90)
const FULL_ROUTE_PATH = buildRoutePath(ROUTE.map((stop) => ({ lon: stop.lon, lat: stop.lat })))

/** Aumento del modo «seguir al trineo». */
const FOLLOW_SCALE = 2.4

const CENTER_X = MAP_VIEWBOX.x + MAP_VIEWBOX.width / 2
const CENTER_Y = MAP_VIEWBOX.y + MAP_VIEWBOX.height / 2

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/**
 * Transformación de la capa del mundo. Se usa `transform` en un `<g>` y no un
 * `viewBox` animado porque `transform` sí lo interpolan las transiciones CSS.
 */
function zoomTransform(position: SleighPosition, follow: boolean): string {
  if (!follow) return 'translate(0 0) scale(1)'

  const scale = FOLLOW_SCALE
  const halfWidth = MAP_VIEWBOX.width / (2 * scale)
  const halfHeight = MAP_VIEWBOX.height / (2 * scale)
  const focusX = clamp(
    projectX(position.lon),
    MAP_VIEWBOX.x + halfWidth,
    MAP_VIEWBOX.x + MAP_VIEWBOX.width - halfWidth
  )
  const focusY = clamp(
    projectY(position.lat),
    MAP_VIEWBOX.y + halfHeight,
    MAP_VIEWBOX.y + MAP_VIEWBOX.height - halfHeight
  )

  return `translate(${(CENTER_X - scale * focusX).toFixed(2)} ${(CENTER_Y - scale * focusY).toFixed(2)}) scale(${scale})`
}

const StarField = memo(function StarField({ animate }: { animate: boolean }) {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden
    >
      {STARS.map((star, index) => (
        <circle
          key={index}
          cx={star.x}
          cy={star.y}
          r={star.r}
          fill="#e8f2ff"
          opacity={0.75}
          className={animate ? 'twinkle' : undefined}
          style={animate ? { animationDelay: `${star.delay}s` } : undefined}
        />
      ))}
    </svg>
  )
})

const WorldShapes = memo(function WorldShapes() {
  return (
    <g aria-hidden>
      <g stroke="#5eead4" strokeWidth="0.18" opacity="0.1">
        {GRID_MERIDIANS.map((lon) => (
          <line
            key={`m-${lon}`}
            x1={projectX(lon)}
            y1={MAP_VIEWBOX.y}
            x2={projectX(lon)}
            y2={MAP_VIEWBOX.y + MAP_VIEWBOX.height}
          />
        ))}
        {GRID_PARALLELS.map((lat) => (
          <line
            key={`p-${lat}`}
            x1={0}
            y1={projectY(lat)}
            x2={MAP_WIDTH}
            y2={projectY(lat)}
          />
        ))}
      </g>
      <g fill="url(#tracker-land)" stroke="#7dd3fc" strokeWidth="0.25" strokeOpacity="0.35">
        {LANDMASS_PATHS.map((landmass) => (
          <path key={landmass.id} d={landmass.d} />
        ))}
      </g>
    </g>
  )
})

const Aurora = memo(function Aurora({ boosted, animate }: { boosted: boolean; animate: boolean }) {
  return (
    <g aria-hidden opacity={boosted ? 1 : 0.45} className="transition-opacity duration-700">
      <ellipse
        cx={projectX(0)}
        cy={projectY(80)}
        rx={150}
        ry={16}
        fill="url(#tracker-aurora)"
        className={animate ? 'tracker-aurora' : undefined}
      />
      <ellipse
        cx={projectX(-40)}
        cy={projectY(76)}
        rx={90}
        ry={10}
        fill="url(#tracker-aurora)"
        opacity="0.7"
        className={animate ? 'tracker-aurora' : undefined}
        style={animate ? { animationDelay: '2.4s' } : undefined}
      />
    </g>
  )
})

function StopMarkers({
  state,
  copy,
  selectedStopId,
  onSelectStop,
}: {
  state: TrackerState
  copy: TrackerCopy
  selectedStopId: string | null
  onSelectStop: (stopId: string) => void
}) {
  return (
    <g>
      {DELIVERY_STOPS.map((stop) => {
        const status = stopStatusAt(stop, state)
        const { x, y } = project(stop.lon, stop.lat)
        const selected = selectedStopId === stop.id
        const fill =
          status === 'current' ? '#fde68a' : status === 'visited' ? '#fbbf24' : '#7dd3fc'
        // Radios en grados: a 1000 px de ancho son 3-8 px. Más pequeños no se
        // ven en un móvil; más grandes y Europa se convierte en una mancha.
        const radius = status === 'current' ? 2.8 : status === 'visited' ? 1.7 : 1.3

        return (
          <g key={stop.id} onClick={() => onSelectStop(stop.id)} className="cursor-pointer">
            {status === 'visited' && (
              <circle cx={x} cy={y} r={radius * 2.4} fill={fill} opacity="0.18" />
            )}
            <circle
              cx={x}
              cy={y}
              r={radius}
              fill={fill}
              opacity={status === 'upcoming' ? 0.55 : 1}
              filter={status === 'upcoming' ? undefined : 'url(#tracker-glow)'}
            />
            {selected && (
              <circle
                cx={x}
                cy={y}
                r={radius + 2}
                fill="none"
                stroke="#ffffff"
                strokeWidth="0.6"
                vectorEffect="non-scaling-stroke"
              />
            )}
            {/* Área táctil generosa; invisible pero clicable. */}
            <circle cx={x} cy={y} r={5} fill="transparent">
              <title>{`${copy.stops[stop.id]} — ${copy.map.stopStatus[status]}`}</title>
            </circle>
          </g>
        )
      })}
    </g>
  )
}

function NorthPole({
  onTap,
  label,
  animate,
}: {
  onTap: () => void
  label: string
  animate: boolean
}) {
  const { x, y } = project(NORTH_POLE_LON, NORTH_POLE_LAT)
  return (
    <g onClick={onTap} className="cursor-pointer">
      <circle
        cx={x}
        cy={y}
        r={6}
        fill="#bae6fd"
        opacity="0.16"
        className={animate ? 'tracker-pulse' : undefined}
      />
      <circle cx={x} cy={y} r={2.2} fill="#f8fafc" filter="url(#tracker-glow)" />
      <path
        d={`M${x - 3.4},${y} h6.8 M${x},${y - 3.4} v6.8 M${x - 2.4},${y - 2.4} l4.8,4.8 M${x + 2.4},${y - 2.4} l-4.8,4.8`}
        stroke="#e0f2fe"
        strokeWidth="0.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* La etiqueta va DEBAJO: encima se saldría del recorte del mapa (el
          polo está a 3 unidades del borde superior del viewBox). */}
      <text
        x={x}
        y={y + 10}
        textAnchor="middle"
        fill="#e0f2fe"
        fontSize="5.5"
        opacity="0.9"
        className="select-none"
      >
        {label}
      </text>
      <circle cx={x} cy={y} r={9} fill="transparent">
        <title>{label}</title>
      </circle>
    </g>
  )
}

/**
 * Cuando el trineo está en casa se dibuja **aparcado al lado** del Polo Norte,
 * no encima. Si no, tapa la estrella del polo y, peor, su área táctil se come
 * la del polo: dejarían de funcionar tanto la etiqueta como la sorpresa de la
 * aurora, que es justo lo único que hay que tocar la mayor parte del año.
 */
const PARKING_OFFSET = { lon: 26, lat: -4 }

function Sleigh({
  position,
  parked,
  trail,
  onTap,
  label,
  animate,
  moving,
}: {
  position: SleighPosition
  parked: boolean
  trail: readonly SleighPosition[]
  onTap: () => void
  label: string
  animate: boolean
  moving: boolean
}) {
  const { x, y } = parked
    ? project(position.lon + PARKING_OFFSET.lon, position.lat + PARKING_OFFSET.lat)
    : project(position.lon, position.lat)

  return (
    <g>
      {/* Estela: posiciones reales de hace unos minutos, así que sigue las
          curvas del recorrido en lugar de ser una línea recta detrás. */}
      {trail.map((point, index) => {
        const tail = project(point.lon, point.lat)
        return (
          <circle
            key={index}
            cx={tail.x}
            cy={tail.y}
            r={1.4 - index * 0.32}
            fill="#fde68a"
            opacity={0.55 - index * 0.15}
          />
        )
      })}

      <g
        onClick={onTap}
        className={`cursor-pointer ${animate ? 'transition-transform duration-1000 ease-linear' : ''}`}
        style={{ transform: `translate(${x}px, ${y}px)` }}
      >
        <circle r={7} fill="#fbbf24" opacity="0.14" className={animate ? 'tracker-pulse' : undefined} />
        <g className={animate && moving ? 'tracker-bob' : undefined}>
          {/* Renos */}
          <ellipse cx={5.6} cy={-0.4} rx={2} ry={1} fill="#c8a06a" />
          <path
            d="M4.6,-1.4 l-0.9,-1.6 M5.6,-1.4 l0.4,-1.8"
            stroke="#8b6b3d"
            strokeWidth="0.35"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {/* Nariz de Rodolfo */}
          <circle cx={7.7} cy={-0.3} r={0.8} fill="#f87171" filter="url(#tracker-glow)" />
          {/* Riendas */}
          <path
            d="M3.4,0.1 L0.2,0.9"
            stroke="#fcd34d"
            strokeWidth="0.3"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {/* Trineo */}
          <path d="M-6.2,1.6 q0.4,-3.2 3,-3.2 h3.4 q1,0 1.2,1.4 l0.3,1.8 z" fill="#dc2626" />
          <path
            d="M-6.8,2.4 h8.6 q1.4,0 1.4,-1.2"
            fill="none"
            stroke="#fcd34d"
            strokeWidth="0.5"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {/* Papá Noel */}
          <circle cx={-1.6} cy={-2.4} r={0.85} fill="#fde8d7" />
          <path d="M-2.5,-3 q0.9,-1.1 1.9,0" fill="none" stroke="#dc2626" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
          {/* Saco */}
          <circle cx={-4.4} cy={-0.6} r={1.5} fill="#a16207" />
        </g>
        <circle r={10} fill="transparent">
          <title>{label}</title>
        </circle>
      </g>
    </g>
  )
}

export interface TrackerMapProps {
  copy: TrackerCopy
  state: TrackerState
  trail: readonly SleighPosition[]
  visitedPath: string
  selectedStopId: string | null
  follow: boolean
  animate: boolean
  auroraBoost: boolean
  onSelectStop: (stopId: string) => void
  onSelectSleigh: () => void
  onTapNorthPole: () => void
  /** Descripción textual del estado, para lectores de pantalla. */
  description: string
}

export function TrackerMap({
  copy,
  state,
  trail,
  visitedPath,
  selectedStopId,
  follow,
  animate,
  auroraBoost,
  onSelectStop,
  onSelectSleigh,
  onTapNorthPole,
  description,
}: TrackerMapProps) {
  const transform = useMemo(() => zoomTransform(state.position, follow), [state.position, follow])
  const moving = state.phase === 'traveling'

  return (
    <div className="absolute inset-0">
      <StarField animate={animate} />
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox={MAP_VIEWBOX_ATTR}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={copy.map.ariaLabel}
      >
        <desc>{description}</desc>
        <defs>
          <linearGradient id="tracker-land" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#12314f" />
            <stop offset="100%" stopColor="#0d2440" />
          </linearGradient>
          <linearGradient id="tracker-aurora" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0" />
            <stop offset="35%" stopColor="#34d399" stopOpacity="0.55" />
            <stop offset="65%" stopColor="#a78bfa" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
          </linearGradient>
          <filter id="tracker-glow" x="-120%" y="-120%" width="340%" height="340%">
            <feGaussianBlur stdDeviation="0.9" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g
          transform={transform}
          className={animate ? 'transition-transform duration-700 ease-out' : undefined}
        >
          <Aurora boosted={auroraBoost} animate={animate} />
          <WorldShapes />

          {/* Ruta completa en trazo tenue y discontinuo… */}
          <path
            d={FULL_ROUTE_PATH}
            fill="none"
            stroke="#7dd3fc"
            strokeWidth="0.5"
            strokeOpacity="0.35"
            strokeDasharray="2 3"
            vectorEffect="non-scaling-stroke"
          />
          {/* …y encima, en oro, el trozo que ya ha recorrido. */}
          {visitedPath && (
            <path
              d={visitedPath}
              fill="none"
              stroke="#fbbf24"
              strokeWidth="1"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              filter="url(#tracker-glow)"
            />
          )}

          <StopMarkers
            state={state}
            copy={copy}
            selectedStopId={selectedStopId}
            onSelectStop={onSelectStop}
          />
          <NorthPole onTap={onTapNorthPole} label={copy.map.northPoleLabel} animate={animate} />
          <Sleigh
            position={state.position}
            parked={!moving}
            trail={trail}
            onTap={onSelectSleigh}
            label={copy.map.sleighLabel}
            animate={animate}
            moving={moving}
          />
        </g>
      </svg>
    </div>
  )
}
